import React, { useState } from 'react';
import cs_analysis_fetch from '../../../utils/cs_analysis_fetch';

/**
 * Renders the corpus-grounded sources under an assistant chat reply — the
 * verified quote + tier + deep link behind each factual claim — AND the HITL
 * learning controls: a per-answer 👍/👎 rating and per-citation "wrong / garbled"
 * flags. These POST to /corpus/feedback so rep corrections feed the learning
 * loop (ontology + verifier tuning). Shared by ChatDrawer + the per-finding
 * panel (keep the two surfaces in sync). `citations` = the response's
 * corpusCitations; `ctx` = { query, scope, clientId } for feedback attribution.
 */
// TICKET citations come from upstream customer_ticket rows — presented as
// "product ticket" per the concept rename (enum value unchanged).
const display_source = (s) => (s === 'TICKET' ? 'PRODUCT TICKET' : s);
const TIER_BG = { CONFIRMED: '#e6f4ea', LIKELY: '#fef7e0', POSSIBLE: '#f1f3f4' };
const FB_BTN = { border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 12, padding: 0 };
const FB_LINK = { border: 'none', background: 'transparent', cursor: 'pointer', color: '#9aa5b1', fontSize: 10, textDecoration: 'underline', padding: 0 };

async function postFeedback(body) {
    try {
        await cs_analysis_fetch({ path: '/api/v1/corpus/feedback', method: 'POST', body });
        return true;
    } catch (e) {
        return false;
    }
}

export default function CorpusSources({ citations, ctx }) {
    const [rating, setRating] = useState(null); // 'helpful' | 'not_helpful'
    const [flagged, setFlagged] = useState({}); // { [n]: true }
    if (!Array.isArray(citations) || citations.length === 0) return null;
    const base = ctx || {};
    const canFeedback = !!ctx;

    const rate = (verdict) => {
        setRating(verdict);
        void postFeedback({ kind: 'ANSWER_RATING', verdict, query: base.query, scope: base.scope, clientId: base.clientId });
    };
    const flag = (c, kind) => {
        setFlagged((f) => ({ ...f, [c.n]: true }));
        void postFeedback({
            kind,
            query: base.query,
            scope: base.scope,
            clientId: base.clientId,
            refId: String(c.n),
            note: (c.statement || c.quote || '').slice(0, 200),
        });
    };

    return (
        <div style={{ marginTop: 6, maxWidth: '85%', fontSize: 11, color: '#5f6b7a' }}>
            <div style={{ fontWeight: 600, marginBottom: 3, display: 'flex', gap: 8, alignItems: 'center' }}>
                <span>Sources ({citations.length})</span>
                {canFeedback &&
                    (rating ? (
                        <span style={{ color: '#7a8899', fontWeight: 400 }}>· thanks</span>
                    ) : (
                        <span style={{ color: '#9aa5b1', fontWeight: 400 }}>
                            · helpful?{' '}
                            <button type="button" onClick={() => rate('helpful')} style={FB_BTN} title="Helpful">
                                <span role="img" aria-label="helpful">👍</span>
                            </button>{' '}
                            <button type="button" onClick={() => rate('not_helpful')} style={FB_BTN} title="Not helpful / wrong">
                                <span role="img" aria-label="not helpful">👎</span>
                            </button>
                        </span>
                    ))}
            </div>
            {citations.map((c) => {
                // CSP-sourced citations are authoritative structured fields, not
                // transcript excerpts — the "wrong/garbled" flags (for misread
                // conversation quotes) don't apply, so hide them there.
                const isCsp = (c.sourceType || '').toUpperCase().includes('CSP');
                return (
                <div key={c.n} style={{ marginBottom: 4, lineHeight: 1.35 }}>
                    <span style={{ fontWeight: 600 }}>[{c.n}]</span>{' '}
                    <span style={{ padding: '0 4px', borderRadius: 3, background: TIER_BG[c.tier] || '#f1f3f4', fontSize: 10 }}>
                        {c.tier}
                    </span>{' '}
                    {c.url ? (
                        <a href={c.url} target="_blank" rel="noopener noreferrer">
                            {display_source(c.sourceType)}
                        </a>
                    ) : (
                        display_source(c.sourceType)
                    )}
                    {c.date ? ` · ${c.date}` : ''}
                    {canFeedback && !isCsp &&
                        (flagged[c.n] ? (
                            <span style={{ marginLeft: 6, color: '#b0451c' }}>⚑ flagged</span>
                        ) : (
                            <span style={{ marginLeft: 6 }}>
                                <button type="button" onClick={() => flag(c, 'FALSE_CLIENT')} style={FB_LINK} title="Wrong client / not about them">
                                    wrong
                                </button>
                                {' · '}
                                <button type="button" onClick={() => flag(c, 'GARBLED_ENTITY')} style={FB_LINK} title="Garbled / misread quote">
                                    garbled
                                </button>
                            </span>
                        ))}
                    {c.statement ? <div style={{ color: '#2c3e50' }}>{c.statement}</div> : null}
                    {c.quote ? <div style={{ fontStyle: 'italic', color: '#7a8899' }}>"{c.quote}"</div> : null}
                </div>
                );
            })}
        </div>
    );
}
